import React, { useState } from 'react';
import {
    IonPage,
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonButton,
    IonIcon,
    IonList,
    IonItem,
    IonLabel,
    IonSpinner,
    useIonToast
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { ApiClient } from '../../api/apiClient';
import './SubscriptionPage.css';

interface PricingTier {
    name: string;
    price: number;
    features: string[];
    level: string;
    highlight?: boolean;
}

const pricingTiers: PricingTier[] = [
    {
        name: 'Basic',
        price: 5,
        level: 'basic',
        features: [
            'Up to 10 active links',
            '50MB file limit',
            'Basic link controls',
            'Simple analytics',
            'Email support'
        ]
    },
    {
        name: 'Pro',
        price: 20,
        level: 'pro',
        highlight: true,
        features: [
            'Up to 50 active links',
            '500MB total file size',
            'Password-protected links & adjustable expiries',
            'Custom branding',
            'Enhanced analytics',
            'Up to 5 team members',
            'Priority email support'
        ]
    },
    {
        name: 'Business',
        price: 40,
        level: 'business',
        features: [
            'Unlimited active links',
            'Highest file sizes (GBs)',
            'Advanced security controls',
            'Full white-label & custom domains',
            'Advanced analytics & integrations',
            'API access, webhooks',
            'Unlimited team members',
            'Dedicated account manager & priority support'
        ]
    }
];

export const SubscriptionPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [present] = useIonToast();

    const handleSubscribe = async (tier: PricingTier) => {
        setIsLoading(true);
        try {
            const apiClient = ApiClient.getInstance();
            const response = await apiClient.post('/subscriptions', {
                subscriptionLevel: tier.level,
                successUrl: `${window.location.origin}/verify-subscription?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/subscription`
            });

            // Redirect to Stripe checkout
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Subscription error:', error);
            present({
                message: 'Failed to initiate subscription. Please try again.',
                duration: 3000,
                color: 'danger'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <IonPage>
            <IonContent>
                <div className="subscription-grid">
                    <div className="subscription-header">
                        <img
                            src="/Logo_Horizontal.png"
                            alt="LinkSharePro"
                            className="subscription-logo"
                        />
                        <h1>Choose Your Plan</h1>
                        <p>Select the plan that best fits your needs</p>
                    </div>

                    <IonGrid>
                        <IonRow>
                            {pricingTiers.map((tier) => (
                                <IonCol
                                    size="12"
                                    sizeMd="4"
                                    key={tier.level}
                                    className="ion-padding"
                                >
                                    <IonCard className={`pricing-card ${tier.highlight ? 'highlighted-plan' : ''}`}>
                                        {tier.highlight && (
                                            <div className="most-popular-badge">Most Popular</div>
                                        )}
                                        <IonCardHeader>
                                            <IonCardTitle className="ion-text-center">
                                                <h2>{tier.name}</h2>
                                                <div className="price">
                                                    <span className="currency">£</span>
                                                    <span className="amount">{tier.price}</span>
                                                    <span className="period">/month</span>
                                                </div>
                                            </IonCardTitle>
                                        </IonCardHeader>

                                        <IonCardContent>
                                            <IonList lines="none" className="features-list">
                                                {tier.features.map((feature, index) => (
                                                    <IonItem key={index}>
                                                        <IonIcon
                                                            icon={checkmark}
                                                            slot="start"
                                                            color="success"
                                                        />
                                                        <IonLabel>{feature}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>

                                            <div className="button-container">
                                                <IonButton
                                                    expand="block"
                                                    onClick={() => handleSubscribe(tier)}
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <IonSpinner name="crescent" />
                                                    ) : (
                                                        `Choose ${tier.name}`
                                                    )}
                                                </IonButton>
                                            </div>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            ))}
                        </IonRow>
                    </IonGrid>
                </div>
            </IonContent>
        </IonPage>
    );
}; 